import * as E from 'fp-ts/Either';
import { Alert, Box, LoadingOverlay } from '@mantine/core';
import { useAuth } from '../auth/AuthContext';
import { ReactNode, useEffect, useState } from 'react';
import { IconExclamationCircle } from '@tabler/icons-react';
import { ApiKey, single } from '../api/apiKeys';

type WithKey = (apiKey: ApiKey) => ReactNode;

export const WithApiKey = ({
  id,
  children,
}: {
  id: string;
  children: WithKey;
}) => {
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState<null | { title: string; message: string }>(
    null
  );

  const [apiKey, setApiKey] = useState<ApiKey | null>(null);

  const auth = useAuth();

  const token = auth?.token ?? null;

  useEffect(() => {
    if (token) {
      setLoading(true);

      single(token, id)
        .then((result) => {
          if (E.isRight(result)) {
            setApiKey(result.right);
          } else {
            setError({
              title: 'Error loading results',
              message:
                (result.left as any)?.helpMessage ??
                result.left?.message ??
                'Failed to fetch data',
            });
          }
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          setError({
            title: 'Error loading results',
            message: 'Failed to fetch data',
          });
        });
    }
  }, [token, id]);

  // If you want the overlay to be contained, ensure the
  //   parent has a position of relative or absolute
  if (loading) {
    return (
      <LoadingOverlay
        visible={true}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
      />
    );
  }

  return (
    <Box>
      {error ? (
        <Alert
          variant="outline"
          color="red"
          title={error.title}
          icon={<IconExclamationCircle />}
          mb="md"
        >
          {error.message}
        </Alert>
      ) : null}
      {apiKey ? children(apiKey) : null}
    </Box>
  );
};
